import {useWalletTransferCompletedStepOnSuccess} from '#/components/wallet-transfer/use-wallet-transfer-completed-step-on-success';
import {WalletTransferContext} from '#/components/wallet-transfer/wallet-transfer-context';
import {
  ActionNextStepButtonContainer,
  SkipNextButton,
  StepHeader,
  TokenTransferCompoundStepButton,
  TokenTransferSingleStepButton,
  TokenTransferStatusContainer,
  TokenTransferStatusProvider,
  TokenTransferStatusTitle,
} from '#/components/wallet-transfer/wallet-transfer-modal';
import {WalletTransferTab} from '#/components/wallet-transfer/wallet-transfer-tab';
import {WalletContext} from '#/contexts/wallet-provider';
import {ShareLandMarketContract} from '#/contracts/ShareLandMarketContract';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useLandAccountInfos} from '#/hooks/query/use-land-account-info';
import {useLandTokenContracts} from '#/hooks/query/use-land-token-contract';
import {useLands} from '#/hooks/query/use-lands';
import {getIsAddressValid} from '#/utils/account-utils';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {UseMutationResult, useMutation} from '@tanstack/react-query';
import {FunctionComponent, useContext, useEffect, useState} from 'react';
import {Title} from 'react-head';
import {useTranslation} from 'react-i18next';
import {Land, LandAccountInfo} from 'shareland-common';
import {waitForTransactionReceipt} from 'wagmi/actions';

type LandAccountInfoContract = {
  land: Land;
  landAccountInfo: LandAccountInfo;
  shareLandContract: ShareLandMarketContract;
};

export const WalletTransferStepShareland: FunctionComponent = () => {
  const {
    accountResult: {address: account},
  } = useContext(WalletContext);
  const {address} = useContext(WalletTransferContext);

  const {t} = useTranslation('translation');

  const invalidateQueries = useInvalidateQueries();

  const {data: lands = [], isLoading: landsIsLoading} = useLands();
  const {data: landAccountInfos = [], isLoading: landAccountInfosIsLoading} = useLandAccountInfos(lands, account);
  const landAndAccountInfos = lands
    .map((land, index) => ({
      land,
      landAccountInfo: landAccountInfos[index],
    }))
    .filter(
      ({landAccountInfo}) =>
        !!landAccountInfo && (landAccountInfo.landTokenAmount > 0 || landAccountInfo.lpTokenAmount > 0),
    ) as Omit<LandAccountInfoContract, 'shareLandContract'>[];

  const shareLandContracts = useLandTokenContracts(landAndAccountInfos.map(({land}) => land));

  const landAccountInfoContracts = landAndAccountInfos.map((landAccountInfo, index) => ({
    ...landAccountInfo,
    shareLandContract: shareLandContracts[index],
  })) as LandAccountInfoContract[];

  const isLoading = landsIsLoading || landAccountInfosIsLoading;

  const transferShareLandEnabled =
    !!account && getIsAddressValid(address) && !!landAccountInfoContracts?.length && !!account;
  const transferShareLandMutation = useMutation({
    mutationKey: ['transfer-shareland'],
    mutationFn: async (params: {landAccountInfo: LandAccountInfo; shareLandContract: ShareLandMarketContract}) => {
      const {landAccountInfo, shareLandContract} = params;

      if (!transferShareLandEnabled) {
        throw new Error('Transfer ShareLand is not enabled');
      }

      if (landAccountInfo.landTokenAmount > 0) {
        const hash = await shareLandContract.transferShareLandToken(
          address,
          Math.round(landAccountInfo.landTokenAmount),
        );

        await waitForTransactionReceipt(wagmiConfig, {
          hash,
        });
      }

      if (landAccountInfo.borrowedLandTokenAmount > 0) {
        const hash = await shareLandContract.transferLiquidityProviderToken(
          address,
          Math.round(landAccountInfo.lpTokenAmount),
        );

        await waitForTransactionReceipt(wagmiConfig, {
          hash,
        });
      }

      return true;
    },
  });

  type TransferOperation = {
    tokenSymbol: string;
    data: any;
    status: UseMutationResult['status'];
    error: Error | null;
  };
  const [transferOperations, setTransferOperations] = useState<TransferOperation[]>([]);

  const updateLandOperation = (transferOperation: TransferOperation) => {
    setTransferOperations(prev => {
      const index = prev.findIndex(({tokenSymbol}) => tokenSymbol === transferOperation.tokenSymbol);

      if (index === -1) {
        return prev;
      }

      const copy = [...prev];
      copy[index] = transferOperation;
      return copy;
    });
  };

  const runTransferOperationForLand = async ({land, landAccountInfo, shareLandContract}: LandAccountInfoContract) => {
    updateLandOperation({
      tokenSymbol: land.tokenSymbol,
      data: null,
      status: 'pending',
      error: null,
    });

    try {
      const res = await transferShareLandMutation.mutateAsync({landAccountInfo, shareLandContract});

      updateLandOperation({
        tokenSymbol: land.tokenSymbol,
        data: res,
        status: 'success',
        error: null,
      });
    } catch (e) {
      updateLandOperation({
        tokenSymbol: land.tokenSymbol,
        data: null,
        status: 'error',
        error: e as Error,
      });
    }
  };

  const runAllSharelandTransfers = async () => {
    if (!transferOperations.length) {
      setTransferOperations(
        landAccountInfoContracts.map(({land: {tokenSymbol}}) => {
          return {
            tokenSymbol,
            data: null,
            status: 'idle',
            error: null,
          };
        }),
      );
    }

    for await (const landAccountInfoContract of landAccountInfoContracts.filter(
      val => transferOperations.find(({tokenSymbol}) => tokenSymbol === val.land.tokenSymbol)?.status !== 'success',
    )) {
      await runTransferOperationForLand(landAccountInfoContract);
    }
  };

  const queryStatus = (() => {
    if (
      (!!transferOperations.length && transferOperations.every(({status}) => status === 'success')) ||
      (!landAccountInfoContracts.length && !isLoading)
    ) {
      return 'success';
    }
    if (transferOperations.some(({status}) => status === 'pending')) {
      return 'loading';
    }
    if (transferOperations.some(({status}) => status === 'error')) {
      return 'error';
    }
    return 'idle';
  })();

  useWalletTransferCompletedStepOnSuccess(queryStatus === 'success', WalletTransferTab.SHARELAND);

  useEffect(() => {
    return () => {
      if (queryStatus === 'success') {
        landAccountInfoContracts.forEach(({land: {id}}) => {
          invalidateQueries.useLandMarketInfos(id);
          invalidateQueries.useLandAccountInfos(account, id);
        });
      }
    };
  }, [account, invalidateQueries, landAccountInfoContracts, queryStatus]);

  return (
    <>
      <StepHeader>{t('pages.walletTransfer.sections.assets.header')}</StepHeader>

      {landAccountInfoContracts.length > 0 ? (
        <>
          <Title>{t('pages.walletTransfer.sections.assets.explanation')}</Title>

          <div className='grow space-y-2 mb-2'>
            {landAccountInfoContracts.map(({land, landAccountInfo, shareLandContract}, index) => {
              return (
                <TokenTransferStatusProvider
                  key={index}
                  queryEnabled={transferShareLandEnabled}
                  queryStatus={transferOperations[index]?.status ?? 'idle'}
                  queryError={transferOperations[index]?.error ?? null}
                  queryStart={() => {
                    runTransferOperationForLand({land, landAccountInfo, shareLandContract});
                  }}
                >
                  <TokenTransferStatusContainer>
                    <TokenTransferStatusTitle>{land.name}</TokenTransferStatusTitle>
                    <TokenTransferCompoundStepButton />
                  </TokenTransferStatusContainer>
                </TokenTransferStatusProvider>
              );
            })}
          </div>

          <ActionNextStepButtonContainer>
            <TokenTransferStatusProvider
              queryEnabled={transferShareLandEnabled}
              queryStatus={queryStatus}
              queryError={transferOperations.find(val => ['error'].includes(val.status))?.error ?? null}
              queryStart={() => {
                runAllSharelandTransfers();
              }}
            >
              <TokenTransferSingleStepButton />
            </TokenTransferStatusProvider>
            <SkipNextButton active={queryStatus === 'success'} />
          </ActionNextStepButtonContainer>
        </>
      ) : (
        <>
          <div className='grow flex-center text-base font-bold text-gray-400'>
            {t('pages.walletTransfer.sections.assets.noAssetsToTransfer')}
          </div>
          <ActionNextStepButtonContainer>
            <SkipNextButton active={true} />
          </ActionNextStepButtonContainer>
        </>
      )}
    </>
  );
};
